import React, { useEffect, useRef, useState } from 'react';
import { Button, FloatingLabel, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAddOrderNoteMutation } from '../../../../../slices/ordersApiSlice';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOrderContext } from '../../../../../providers/OrderProvider';

const MAX_LENGTH = 500; // 最大字符数限制

const schema = yup.object({
	orderNote: yup.string().required('添加的备注不能为空').trim('备注不能只包含空格').min(1, '备注不能为空').max(MAX_LENGTH, `备注不能超过 ${MAX_LENGTH} 个字符`),
});

const OrderAddNote = ({ type }) => {
	const { order, isOrderReady } = useOrderContext();
	const _id = order?._id;
	const actionDisabled = !isOrderReady;

	const textareaRef = useRef(null);
	const [noteLength, setNoteLength] = useState(0);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isSubmitting },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: { orderNote: '' },
	});

	const [addOrderNote, { isLoading }] = useAddOrderNoteMutation();

	// 监听输入变化以更新字符计数
	const orderNoteValue = watch('orderNote');
	useEffect(() => {
		setNoteLength(orderNoteValue?.length || 0);
	}, [orderNoteValue]);

	// 自动聚焦到文本框
	useEffect(() => {
		if (textareaRef.current) {
			textareaRef.current.focus();
		}
	}, []);

	// 键盘快捷键支持：Ctrl+Enter 或 Cmd+Enter 提交
	const handleKeyDown = (e) => {
		if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
			e.preventDefault();
			handleSubmit(onSubmit)();
		}
	};

	const onSubmit = async (data) => {
		try {
			const trimmedNote = data.orderNote.trim();
			await addOrderNote({ _id, data: { orderNote: trimmedNote }, type }).unwrap();

			toast.success('备注添加成功');
			reset({ orderNote: '' });
			setNoteLength(0);

			// 重新聚焦到文本框
			setTimeout(() => {
				if (textareaRef.current) {
					textareaRef.current.focus();
				}
			}, 100);
		} catch (error) {
			const errorMessage = error?.data?.message || error?.message || '添加备注失败，请稍后重试';
			toast.error(errorMessage);
		}
	};

	const handleClear = () => {
		reset({ orderNote: '' });
		setNoteLength(0);
		if (textareaRef.current) {
			textareaRef.current.focus();
		}
	};

	const isSubmittingForm = isSubmitting || isLoading;
	const isNearLimit = noteLength > MAX_LENGTH * 0.8;
	const isOverLimit = noteLength > MAX_LENGTH;
	const canSubmit = noteLength > 0 && !isOverLimit && !isSubmittingForm && !actionDisabled;

	return (
		<Form onSubmit={handleSubmit(onSubmit)} className='order-add-note-form'>
			<Form.Group className='mt-2'>
				<FloatingLabel controlId='orderNote' label='添加备注'>
					<Form.Control
						ref={textareaRef}
						size='text'
						name='orderNote'
						as='textarea'
						rows={3}
						style={{
							height: '80px',
							minHeight: '80px',
							maxHeight: '80px',
							paddingRight: orderNoteValue ? '40px' : '12px',
							fontSize: '0.95rem',
							overflow: 'hidden',
						}}
						isInvalid={!!errors.orderNote || isOverLimit}
						disabled={actionDisabled || isSubmittingForm}
						{...register('orderNote')}
						onKeyDown={handleKeyDown}
						placeholder='输入备注内容，按 Ctrl+Enter 快速提交...'
						maxLength={MAX_LENGTH + 10} // 允许稍微超过以便显示错误
					/>
					{orderNoteValue && (
						<Button
							variant='link'
							className='position-absolute'
							style={{
								top: '8px',
								right: '8px',
								padding: '4px 8px',
								zIndex: 10,
								opacity: 0.6,
							}}
							onClick={handleClear}
							disabled={actionDisabled || isSubmittingForm}
							title='清除内容'
						>
							<FontAwesomeIcon icon='times' size='sm' />
						</Button>
					)}
					<Form.Control.Feedback type='invalid'>{errors.orderNote?.message || (isOverLimit ? `备注不能超过 ${MAX_LENGTH} 个字符` : '')}</Form.Control.Feedback>
				</FloatingLabel>
				<div className='d-flex justify-content-between align-items-center mt-1'>
					<div className='d-flex align-items-center gap-2'>
						<small className={`text-muted ${isNearLimit ? 'text-warning' : ''} ${isOverLimit ? 'text-danger' : ''}`}>
							<FontAwesomeIcon icon='sticky-note' className='me-1' size='xs' />
							{noteLength} / {MAX_LENGTH}
						</small>
						{noteLength > 0 && (
							<small className='text-muted'>
								<FontAwesomeIcon icon='keyboard' className='me-1' size='xs' />
								Ctrl+Enter 提交
							</small>
						)}
					</div>
				</div>
			</Form.Group>

			<div className='d-flex justify-content-end gap-2'>
				{orderNoteValue && (
					<Button variant='outline-secondary' size='sm' onClick={handleClear} disabled={isSubmittingForm} type='button'>
						<FontAwesomeIcon icon='times' className='me-1' />
						清除
					</Button>
				)}
				<Button size='sm' type='submit' disabled={!canSubmit} variant={canSubmit ? 'primary' : 'secondary'} style={{ padding: '2px 10px', fontSize: '0.85rem', minWidth: 'unset' }}>
					{isSubmittingForm ? <FontAwesomeIcon icon='spinner' spin /> : <FontAwesomeIcon icon='paper-plane' />}
				</Button>
			</div>
		</Form>
	);
};

export default OrderAddNote;
